/**
 * © [2023] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */
import React from 'react';
import * as Styles from '../../../../SharedModules/StyleConstant/StyleConstant';
import {
  textMatcher,
  cellRenderer,
} from '../../../../SharedModules/utilities/utilities';

// Custom Comparator for Quarter
const comparatorQtr = (a, b) => {
  const splittedQtrA = a?.split('Q');
  const splittedQtrB = b?.split('Q');
  if (splittedQtrA?.length > 0) {
    return (
      splittedQtrA[1] - splittedQtrB[1] || splittedQtrA[0] - splittedQtrB[0]
    );
  }
  return null;
};

const fourDecimalRenderer = (params, a) => {
  const { value } = params;
  const { isTextColorRed } = params.colDef;
  const { printPrefixValue } = params.colDef;
  const { printPostfixValue } = params.colDef;
  if (value === '') return '';
  if (value < 0) {
    return (
      <span
        style={{
          color: isTextColorRed ? '#eb0000' : '',
          float: 'right',
        }}
      >
        {`(${printPrefixValue ? `${printPrefixValue} ` : ''}${parseFloat(value)
          .toLocaleString('en-US', {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
          })
          .substring(1)})` + `${printPostfixValue || ''}`}
      </span>
    );
  }
  if (value > -1) {
    return (
      <span style={{ float: 'right' }}>
        {`${
          (printPrefixValue ? `${printPrefixValue} ` : '') +
          parseFloat(value).toLocaleString('en-US', {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
          })
        }${printPostfixValue || ''}`}
      </span>
    );
  }
  return params.value || '';
};

const comparator = (a, b) => {
  return parseFloat(a) - parseFloat(b);
};


const dateComparatorAsc = (a, b) => {
  const toTime = (v) => {
    if (!v) return Number.POSITIVE_INFINITY; // null/empty last on asc
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  return toTime(a) - toTime(b);
};


// Table head for NDC report

// Quarter		Effective Quarter	URA	Number Of Units	Change Of Units	Dispute Code	Dollar Amount
export const TableHead = [
  
{
    headerName: 'Quarter',
    field: 'targetQtr',
    editable: false,
    suppressMenu: false,
    sortable: true,
    minWidth: Styles.Width_120,
    sort: 'desc',     // primary sort order
    sortIndex: 0,     // primary priority
    cellRenderer: 'GenericCodeTextBox',
    comparator: comparatorQtr,
    cellEditorParams: {
      maxLength: 5,
      custWidth: '135px',
    },
    cellStyle: { borderRightColor: Styles.BorderColor },
    wrapText: true,
    autoHeight: true,
    custClass: 'tbill-rate-editable-input',
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Activity Type',
    field: 'drugActivityTypeCd',
    editable: false,
    suppressMenu: false,
    sortable: true,
    // columnSpan: 2,
    minWidth: Styles.Width_210,
    wrapText: true,
    autoHeight: true,
    cellEditorParams: {
      maxLength: 5,
      custWidth: '135px',
    },
    cellRenderer: 'TextBoxRender',
    cellClass: 'frr-ellipsis-text',
    cellStyle: { borderRightColor: Styles.BorderColor },
    custClass: 'tbill-rate-editable-input',
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },
  // {
  //   headerName: '',
  //   field: '',
  //   removeColumn: true,
  //   minWidth: 1,
  //   resizable: false,
  //   editable: false,
  // },
  
{
    headerName: 'Activity Date',
    field: 'drugActivityDt',
    editable: false,
    suppressMenu: false,
    sortable: true,
    minWidth: Styles.Width_180,
    wrapText: true,
    autoHeight: true,
    sort: 'asc',      // secondary sort order
    sortIndex: 1,     // secondary priority
    comparator: dateComparatorAsc,
    cellStyle: { borderRightColor: Styles.BorderColor },
    custClass: 'tbill-rate-editable-input',
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Effective Quarter',
    field: 'invoiceQtr',
    editable: false,
    suppressMenu: false,
    wrapText: true,
    autoHeight: true,
    comparator: comparatorQtr,
    sortable: true,
    minWidth: Styles.Width_130,
    cellEditorParams: {
      maxLength: 5,
      custWidth: '135px',
    },
    cellStyle: { borderRightColor: Styles.BorderColor },
    custClass: 'tbill-rate-editable-input',
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'URA',
    field: 'unitRebateAmt',
    editable: false,
    suppressMenu: false,
    wrapText: true,
    autoHeight: true,
    sortable: true,
    excelAlignment: 'right',
    isTextColorRed: true,
    minWidth: Styles.Width_100,
    alignment: 'right',
    cellEditorParams: {
      maxLength: 5,
      custWidth: '135px',
    },
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    custClass: 'tbill-rate-editable-input',
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
      textMatcher,
    },
  },

  {
    headerName: 'Number Of Units',
    field: 'numOfUnits',
    editable: false,
    wrapText: true,
    autoHeight: true,
    suppressMenu: false,
    sortable: true,
    minWidth: Styles.Width_125,
    isTextColorRed: true,
    excelAlignment: 'right',
    isCount: true,
    alignment: 'right',
    cellEditorParams: {
      maxLength: 5,
      custWidth: '135px',
    },
    cellRenderer: fourDecimalRenderer,
    comparator,
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    custClass: 'tbill-rate-editable-input',
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Change Of Units',
    field: 'changeNumOfUnits',
    editable: false,
    wrapText: true,
    autoHeight: true,
    suppressMenu: false,
    sortable: true,
    excelAlignment: 'right',
    minWidth: Styles.Width_120,
    isTextColorRed: true,
    isCount: true,
    alignment: 'right',
    cellEditorParams: {
      maxLength: 5,
      custWidth: '135px',
    },
    cellRenderer: fourDecimalRenderer,
    comparator,
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    custClass: 'tbill-rate-editable-input',
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Dispute Code',
    field: 'disputeCode',
    editable: false,
    wrapText: true,
    autoHeight: true,
    suppressMenu: false,
    sortable: true,
    minWidth: Styles.Width_120,
    cellEditorParams: {
      maxLength: 5,
      custWidth: '135px',
    },
    cellStyle: { borderRightColor: Styles.BorderColor },
    custClass: 'tbill-rate-editable-input',
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Dollar Amount',
    field: 'changeAmt',
    editable: false,
    wrapText: true,
    // autoHeight: true,
    suppressMenu: false,
    sortable: true,
    minWidth: Styles.Width_125,
    isTextColorRed: true,
    isAmount: true,
    alignment: 'right',
    cellRenderer,
    comparator,
    isTextColorRed: true,
    printPrefixValue: '$',
    cellEditorParams: {
      maxLength: 5,
      custWidth: '135px',
    },
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    custClass: 'tbill-rate-editable-input',
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
      textMatcher,
    },
  },
];
